import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  AccountingClosingView,
  CashFlowReportView,
  DreReportView,
  type ClosingDestination,
  type ReportDestination,
} from '../modules/accounting/index.js';
import { businessUnitApi } from '../lib/api.js';
import { refreshSession } from '../lib/session.js';
import { useCompanyScope } from './company-context.js';
import { GatedCompanyScreen } from './gated-company-screen.js';
import type { CashFlowView, DreView } from './list-views.js';
import type { ViewSetter } from './view-state.js';

const DESTINATIONS = {
  classification: '/companies/$companyKey/classification',
  finance: '/companies/$companyKey/finance',
  payables: '/companies/$companyKey/payables',
  receivables: '/companies/$companyKey/receivables',
  closing: '/companies/$companyKey/accounting-closing',
} as const satisfies Record<ReportDestination, string>;

/// O DRE e o Fluxo de caixa leem a unidade escolhida no topo ou, sem ela, todas as que a pessoa
/// alcança (ADR 0039); o ano vem do mês de trabalho da casca (ADR 0020).
function useOpen() {
  const { company } = useCompanyScope();
  const navigate = useNavigate();
  return (destination: ReportDestination) => {
    void navigate({ to: DESTINATIONS[destination], params: { companyKey: company.key } });
  };
}

export function CompanyDreScreen({ view, onView }: { view: DreView; onView: ViewSetter<DreView> }) {
  const { can, today } = useCompanyScope();
  const open = useOpen();
  return (
    <GatedCompanyScreen allowed={can('accounting.reports.read')}>
      <DreReportView view={view} onView={onView} today={today} onOpen={open} />
    </GatedCompanyScreen>
  );
}

export function CompanyCashFlowScreen({
  view,
  onView,
}: {
  view: CashFlowView;
  onView: ViewSetter<CashFlowView>;
}) {
  const { can, today } = useCompanyScope();
  const open = useOpen();
  return (
    <GatedCompanyScreen allowed={can('accounting.reports.read')}>
      <CashFlowReportView view={view} onView={onView} today={today} onOpen={open} />
    </GatedCompanyScreen>
  );
}

/// O fechamento contábil (ADR 0064). Cada unidade fecha o próprio mês: a tabela das unidades abre
/// uma unidade trocando a da sessão, como o seletor do topo faz.
export function CompanyAccountingClosingScreen() {
  const { can, today, company } = useCompanyScope();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const switchUnit = useMutation({
    mutationFn: businessUnitApi.switch,
    onSuccess: async () => {
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
      await refreshSession(queryClient);
    },
  });
  const open = (destination: ClosingDestination) => {
    const params = { companyKey: company.key };
    if (destination === 'classification')
      void navigate({ to: '/companies/$companyKey/classification', params });
    else if (destination === 'commissions')
      void navigate({ to: '/companies/$companyKey/commissions', params });
    else if (destination === 'payables')
      void navigate({ to: '/companies/$companyKey/payables', params });
    else if (destination === 'receivables')
      void navigate({ to: '/companies/$companyKey/receivables', params });
    else if (destination === 'dre') void navigate({ to: '/companies/$companyKey/dre', params });
    else
      void navigate({
        to: '/companies/$companyKey/finance',
        params,
        search: { tab: destination === 'cash-closing' ? 'closing' : 'reconciliation' },
      });
  };
  return (
    <GatedCompanyScreen allowed={can('accounting.reports.read')}>
      <AccountingClosingView
        today={today}
        activeUnitId={company.activeBusinessUnitId ?? null}
        unitCount={company.businessUnits?.length ?? 1}
        can={{
          close: can('accounting.closing.close'),
          reopen: can('accounting.closing.reopen'),
          adjust: can('accounting.closing.adjust'),
          commissions: can('sales.commissions.read'),
          obligations: can('financial_obligations.read'),
        }}
        onOpen={open}
        onOpenUnit={(unitId) => switchUnit.mutate(unitId)}
        switching={switchUnit.isPending}
      />
    </GatedCompanyScreen>
  );
}
